import { useState, useEffect, useContext, createContext } from 'react'
import { api, setToken, setOnUnauthorized } from '../api/client'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const auth = useAuthInternal()
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

// In-memory auth state (module-level singleton)
let _currentUser = null
let _isAuthenticated = false

function useAuthInternal() {
  const [state, setState] = useState({
    user: _currentUser,
    isAuthenticated: _isAuthenticated,
    loading: true,
    showLogin: false,
  })

  useEffect(() => {
    // Register unauthorized handler to show login modal
    setOnUnauthorized(() => {
      setState((s) => ({ ...s, showLogin: true, user: null, isAuthenticated: false }))
    })

    // Check if user is already logged in
    let mounted = true
    const checkAuth = async () => {
      try {
        const user = await api.getMe()
        if (mounted) {
          _currentUser = user
          _isAuthenticated = true
          setState({ user, isAuthenticated: true, loading: false, showLogin: false })
        }
      } catch {
        if (mounted) {
          _currentUser = null
          _isAuthenticated = false
          setState({ user: null, isAuthenticated: false, loading: false, showLogin: false })
        }
      }
    }

    if (_currentUser) {
      setState((s) => ({ ...s, loading: false }))
    } else {
      checkAuth()
    }

    return () => {
      mounted = false
    }
  }, [])

  const login = async (email, password) => {
    const result = await api.login(email, password)
    if (result.two_factor_required) {
      return { two_factor_required: true, ...result }
    }
    setToken(result.access_token)
    _currentUser = result.user
    _isAuthenticated = true
    setState({ user: result.user, isAuthenticated: true, loading: false, showLogin: false })
    return result
  }

  // 开了 2FA 的账号，login 只给一枚 5 分钟受限临时令牌；把验证码换成正牌会话
  // 是登录的第二步，缺了它用户就被扔回落地页、再也没有输入验证码的入口。
  const verifyTwoFactor = async (tempToken, identifier, code) => {
    const result = await api.verify2FALogin(tempToken, identifier, code)
    setToken(result.access_token)
    _currentUser = result.user
    _isAuthenticated = true
    setState({ user: result.user, isAuthenticated: true, loading: false, showLogin: false })
    return result
  }

  const register = async (email, password, name) => {
    const result = await api.register(email, password, name)
    setToken(result.access_token)
    const user = await api.getMe()
    _currentUser = user
    _isAuthenticated = true
    setState({ user, isAuthenticated: true, loading: false, showLogin: false })
    return result
  }

  const loginWithToken = async (token) => {
    setToken(token)
    const user = await api.getMe()
    _currentUser = user
    _isAuthenticated = true
    setState({ user, isAuthenticated: true, loading: false, showLogin: false })
    return user
  }

  const phoneLogin = async (phone, code) => {
    const result = await api.phoneLogin(phone, code)
    setToken(result.access_token)
    _currentUser = result.user
    _isAuthenticated = true
    setState({ user: result.user, isAuthenticated: true, loading: false, showLogin: false })
    return result
  }

  const refreshUser = async () => {
    const user = await api.getMe()
    _currentUser = user
    setState((s) => ({ ...s, user }))
    return user
  }

  const logout = async () => {
    // 先拿还没清的 token 去后端吊销，再清本地 —— 顺序反了就没法吊销了。
    // 后端不可达也必须清本地：安全上"退出"至少要在本机生效，
    // 不能因为一次网络失败让退出按钮失灵。
    try {
      await api.logout()
    } catch (e) {
      console.warn('后端令牌吊销失败，本地会话仍已清除', e)
    }
    setToken(null)
    _currentUser = null
    _isAuthenticated = false
    setState({ user: null, isAuthenticated: false, loading: false, showLogin: false })
  }

  const openLogin = () => {
    setState((s) => ({ ...s, showLogin: true }))
  }

  const closeLogin = () => {
    setState((s) => ({ ...s, showLogin: false }))
  }

  // ── RBAC: permissions from user object (GET /auth/me) ──
  const permissions = state.user?.permissions || []
  const hasPermission = (perm) => {
    if (!perm) return true
    if (!state.user) return false
    // Platform admins bypass tenant permission checks
    if (state.user.is_platform_admin) return true
    return permissions.includes(perm)
  }

  return {
    user: state.user,
    permissions,
    hasPermission,
    isAuthenticated: state.isAuthenticated,
    loading: state.loading,
    showLogin: state.showLogin,
    login,
    register,
    loginWithToken,
    phoneLogin,
    verifyTwoFactor,
    refreshUser,
    logout,
    openLogin,
    closeLogin,
  }
}
